import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query'
import { AxiosError, isAxiosError } from 'axios'

import { useNotificationContext } from 'shared/context/Notifications'

import { createCustomer, deleteCustomer, editCustomer, fetchCustomers } from './services'
import { CustomerType } from './types'

export default function useCustomersQuery(): UseQueryResult<CustomerType[]> {
   return useQuery('customers', fetchCustomers)
}

export function useAddCustomerMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      {
         status: number
         data: any
      },
      AxiosError<ErrorType> | Error,
      CustomerType,
      unknown
   >((payload) => createCustomer(payload), {
      onSuccess: (data) => {
         if (data.status === 200) {
            queryClient.invalidateQueries('customers')
            addNotification('successSave', 'success')
         }
         return data.data
      },
      onError: (error) => {
         if (error) {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         }
      },
   })
}

export function useEditCustomerMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<Result, AxiosError<ErrorType> | Error, { payload: CustomerType; customerUuid: string }, unknown>(
      ({ payload, customerUuid }) => editCustomer(customerUuid, payload),
      {
         onSuccess: (data) => {
            if (data.status === 200) {
               queryClient.invalidateQueries('customers')
               addNotification('successSave', 'success')
            }
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}

export function useDeleteCustomerMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<Result, AxiosError<ErrorType> | Error, { customerUuid: string; force?: boolean }, unknown>(
      ({ customerUuid, force }) => deleteCustomer(customerUuid, force),
      {
         onSuccess: (data) => {
            if (data.status === 204) {
               queryClient.invalidateQueries('customers')
               addNotification('successSave', 'success')
            }
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data?.errors[0].code !== 'errors.client.has-linked-cart-reflinks') {
                     addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
                  }
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}
